import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import {
  BookOpenIcon,
  AcademicCapIcon,
  UserIcon,
  ChartBarIcon,
  QuestionMarkCircleIcon,
  CogIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

const HelpIndex = () => {
  const { user, isAuthenticated } = useAuth();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isPrivilegedUser = user && (
    user.role === 'admin' || 
    user.role === 'manager' || 
    user.role === 'partner' || 
    user.role === 'institution' || 
    user.is_staff
  );

  const userHelpSections = [
    {
      title: 'Getting Started',
      description: 'Learn the basics of using GMSP3 Career Guidance',
      icon: BookOpenIcon,
      href: '/help/getting-started',
      topics: ['Account Setup', 'Profile Creation', 'First Steps']
    },
    {
      title: 'Course Browsing & Search',
      description: 'Find and explore training programs that match your interests',
      icon: AcademicCapIcon,
      href: '/help/courses',
      topics: ['Search Filters', 'Course Details', 'Requirements']
    },
    {
      title: 'Course Applications',
      description: 'Step-by-step guide to applying for training programs',
      icon: DocumentTextIcon,
      href: '/help/applications',
      topics: ['Application Process', 'Required Documents', 'Status Tracking']
    },
    {
      title: 'Opportunities & Jobs',
      description: 'Explore job opportunities, apprenticeships, and internships',
      icon: BriefcaseIcon,
      href: '/help/opportunities',
      topics: ['Job Search', 'Applications', 'Wishlist', 'Career Paths']
    },
    {
      title: 'Career Guidance',
      description: 'Get personalized career recommendations and guidance',
      icon: LightBulbIcon,
      href: '/help/career-guidance',
      topics: ['Skills Assessment', 'Career Recommendations', 'Course Matching']
    },
    {
      title: 'Profile Management',
      description: 'Manage your personal information and educational background',
      icon: UserIcon,
      href: '/help/profile',
      topics: ['Personal Details', 'Education History', 'Location Settings']
    },
    {
      title: 'Dashboard & Navigation',
      description: 'Navigate your dashboard and track your progress',
      icon: ChartBarIcon,
      href: '/help/dashboard',
      topics: ['Dashboard Overview', 'Course Progress', 'Announcements']
    },
    {
      title: 'Troubleshooting & FAQ',
      description: 'Common issues and frequently asked questions',
      icon: QuestionMarkCircleIcon,
      href: '/help/faq',
      topics: ['Login Issues', 'Application Problems', 'Technical Support']
    }
  ];

  const adminHelpSections = [
    {
      title: 'Admin Dashboard',
      description: 'Overview of administrative features and capabilities',
      icon: ChartBarIcon,
      href: '/help/admin/dashboard',
      topics: ['Dashboard Overview', 'Key Metrics', 'Quick Actions']
    },
    {
      title: 'Course Management',
      description: 'Create, edit, and manage training programs',
      icon: AcademicCapIcon,
      href: '/help/admin/courses',
      topics: ['Course Creation', 'Capacity Management', 'Course Updates']
    },
    {
      title: 'Opportunity Management',
      description: 'Manage job opportunities, apprenticeships, and internships',
      icon: BriefcaseIcon,
      href: '/help/admin/opportunities',
      topics: ['Creating Opportunities', 'Application Management', 'Employer Relations']
    },
    {
      title: 'User Administration',
      description: 'Manage user accounts, roles, and permissions',
      icon: UserIcon,
      href: '/help/admin/users',
      topics: ['User Management', 'Role Assignment', 'Account Verification']
    },
    {
      title: 'Student Lifecycle',
      description: 'Manage the complete student journey from application to completion',
      icon: DocumentTextIcon,
      href: '/help/admin/lifecycle',
      topics: ['Application Review', 'Acceptance Process', 'Registration Management']
    },
    {
      title: 'Announcements System',
      description: 'Create and manage system-wide announcements',
      icon: BookOpenIcon,
      href: '/help/admin/announcements',
      topics: ['Creating Announcements', 'Targeting Audiences', 'Scheduling']
    },
    {
      title: 'Django Admin Interface',
      description: 'Advanced administration through Django admin panel',
      icon: CogIcon,
      href: '/help/admin/django',
      topics: ['Admin Panel Access', 'Data Management', 'Bulk Operations']
    },
    {
      title: 'Security & Best Practices',
      description: 'Security guidelines and administrative best practices',
      icon: ShieldCheckIcon,
      href: '/help/admin/security',
      topics: ['Access Control', 'Data Protection', 'Audit Trails']
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--background-gray)' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        padding: '80px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.08) 0%, rgba(30, 64, 175, 0.05) 100%)'
        }}></div>
        
        <div className="container" style={{ position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              Help & Documentation
            </h1>
            <p style={{
              fontSize: '18px',
              color: 'var(--text-gray)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Find comprehensive guides, tutorials, and answers to help you make the most of GMSP3 Career Guidance
            </p>
          </div>
        </div>
      </section>

      {/* User Help Section */}
      <section style={{ backgroundColor: 'var(--white)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              User Guide
            </h2>
            <p style={{
              fontSize: '18px',
              color: 'var(--text-gray)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Everything you need to know about using the platform as a learner
            </p>
          </div>

          <div className="help-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '32px'
          }}>
            {userHelpSections.map((section, index) => {
              const Icon = section.icon;
              return (
                <Link
                  key={index}
                  to={section.href}
                  className="card"
                  style={{
                    textDecoration: 'none',
                    transition: 'all 0.3s ease-in-out',
                    borderLeft: '4px solid var(--primary-yellow)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <div className="card-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        background: 'linear-gradient(135deg, var(--primary-yellow) 0%, var(--primary-yellow-dark) 100%)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon style={{ width: '28px', height: '28px', color: 'var(--primary-black)' }} />
                      </div>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: 'var(--primary-black)',
                        margin: 0
                      }}>
                        {section.title}
                      </h3>
                    </div>
                    <p style={{
                      color: 'var(--text-gray)',
                      lineHeight: 1.6,
                      marginBottom: '16px'
                    }}>
                      {section.description}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {section.topics.map((topic, topicIndex) => (
                        <span
                          key={topicIndex}
                          style={{
                            fontSize: '12px',
                            padding: '4px 8px',
                            backgroundColor: 'var(--primary-yellow-light)',
                            color: 'var(--primary-yellow-dark)',
                            borderRadius: '4px',
                            fontWeight: '500'
                          }}
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Admin Help Section - Only show to privileged users */}
      {isAuthenticated && isPrivilegedUser && (
        <section style={{ backgroundColor: 'var(--background-gray)', padding: '80px 0' }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '24px'
              }}>
                Administrator Guide
              </h2>
              <p style={{
                fontSize: '18px',
                color: 'var(--text-gray)',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Advanced features and administrative tools for GCRA staff
              </p>
            </div>

            <div className="help-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '32px'
            }}>
              {adminHelpSections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <Link
                    key={index}
                    to={section.href}
                    className="card"
                    style={{
                      textDecoration: 'none',
                      transition: 'all 0.3s ease-in-out',
                      borderLeft: '4px solid var(--primary-black)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <div className="card-body">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          background: 'linear-gradient(135deg, var(--primary-black) 0%, #374151 100%)',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Icon style={{ width: '28px', height: '28px', color: 'var(--white)' }} />
                        </div>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: 'var(--primary-black)',
                          margin: 0
                        }}>
                          {section.title}
                        </h3>
                      </div>
                      <p style={{
                        color: 'var(--text-gray)',
                        lineHeight: 1.6,
                        marginBottom: '16px'
                      }}>
                        {section.description}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {section.topics.map((topic, topicIndex) => (
                          <span
                            key={topicIndex}
                            style={{
                              fontSize: '12px',
                              padding: '4px 8px',
                              backgroundColor: '#f3f4f6',
                              color: 'var(--primary-black)',
                              borderRadius: '4px',
                              fontWeight: '500'
                            }}
                          >
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Quick Access Section */}
      <section style={{ backgroundColor: 'var(--white)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '24px'
            }}>
              Need Immediate Help?
            </h2>
            <p style={{
              fontSize: '18px',
              color: 'var(--text-gray)',
              marginBottom: '32px',
              maxWidth: '600px',
              margin: '0 auto 32px'
            }}>
              Can't find what you're looking for? Get in touch with our support team
            </p>
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Link to="/help/faq" className="btn btn-primary" style={{
                fontSize: '18px',
                padding: '16px 32px'
              }}>
                View FAQ
              </Link>
              <a href="mailto:support@gcra.gov.za" className="btn btn-outline" style={{
                fontSize: '18px',
                padding: '16px 32px'
              }}>
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpIndex;
